import { WorkloadResourceConfig } from '@cast/types';
import { toNumOrUndefined } from '@cast/utils';

const DEFAULT_ADAPTIVE_NUMERATOR = 0.5;

export const getApplyThresholdPercentage = (
  config: WorkloadResourceConfig | undefined,
  currentRequest: number | undefined
): number | undefined => {
  if (!config) {
    return undefined;
  }

  const request = toNumOrUndefined(currentRequest);
  const requestValid = request !== undefined && request > 0;

  const strategy = config.applyThresholdStrategy;

  if (strategy?.percentageThreshold?.percentage !== undefined) {
    return strategy.percentageThreshold.percentage;
  }

  if (strategy?.defaultAdaptiveThreshold !== undefined && requestValid) {
    return DEFAULT_ADAPTIVE_NUMERATOR / (request + 1);
  }

  if (strategy?.customAdaptiveThreshold && requestValid) {
    const { numerator, denominator, exponent } =
      strategy.customAdaptiveThreshold;
    const base = request + denominator;
    if (base > 0) {
      return numerator / base ** exponent;
    }
  }

  if (typeof config.applyThreshold === 'number') {
    return config.applyThreshold;
  }

  return undefined;
};

type NoChangeReason = {
  deltaPercentage: number;
  thresholdPercentage: number;
  dynamic: boolean;
};

export const getRecommendationNoChangeReason = ({
  requested,
  recommended,
  config,
}: {
  requested?: number;
  recommended?: number;
  config: WorkloadResourceConfig | undefined;
}): NoChangeReason | undefined => {
  if (!requested || requested <= 0) {
    return undefined;
  }
  if (recommended == null) {
    return undefined;
  }

  const threshold = getApplyThresholdPercentage(config, requested);
  if (threshold === undefined) {
    return undefined;
  }

  const delta = Math.abs(recommended - requested) / requested;
  if (delta > threshold) {
    return undefined;
  }

  return {
    deltaPercentage: delta,
    thresholdPercentage: threshold,
    dynamic: !!(
      config?.applyThresholdStrategy?.defaultAdaptiveThreshold ||
      config?.applyThresholdStrategy?.customAdaptiveThreshold
    ),
  };
};

export const formatThresholdPercent = (fraction: number): string =>
  String(Math.round(fraction * 1000) / 10);
